<template>
  <div v-if="canProcessPayments">
    <PaymentDeliveryFeeBanner
      :order-type="orderType"
      :delivery-fee="deliveryFee"
    />

    <PaymentIvaSection
      :iva-enabled="ivaEnabled"
      :iva-rate="ivaRate"
      :subtotal="subtotal"
      :iva-amount="ivaAmount"
      :order-total="orderTotal"
      :delivery-fee="deliveryFee"
      :current-discount="currentDiscount"
      @toggle-iva="emit('toggle-iva', $event)"
    />

    <PaymentMethodsPanel
      ref="paymentMethodsPanelRef"
      :mark-as-paid="markAsPaid"
      :selected-payment-method="selectedPaymentMethod"
      :order-total="orderTotal"
      :cash-received="currentCashReceived"
      :change-amount="changeAmount"
      :disable-toggle="disableToggle"
      :hide-title="hideTitle"
      :payment-methods="paymentMethods"
      @update:mark-as-paid="emit('update:mark-as-paid', $event)"
      @update:selected-payment-method="emit('update:selected-payment-method', $event)"
      @update:cash-received="emit('update:cash-received', $event)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { Component } from 'vue';
import {
  BanknotesIcon,
  CreditCardIcon,
  DevicePhoneMobileIcon,
  EllipsisHorizontalIcon
} from '@heroicons/vue/24/outline';
import { usePaymentMethods, type PaymentMethod } from '@/composables/usePaymentMethods';
import PaymentDeliveryFeeBanner from './PaymentDeliveryFeeBanner.vue';
import PaymentIvaSection from './PaymentIvaSection.vue';
import PaymentMethodsPanel from './PaymentMethodsPanel.vue';

interface Props {
  isEditMode: boolean;
  canProcessPayments: boolean;
  markAsPaid: boolean;
  selectedPaymentMethod: PaymentMethod;
  orderTotal: number;
  cashReceived?: number;
  disableToggle?: boolean;
  hideTitle?: boolean;
  ivaEnabled?: boolean;
  ivaRate?: number;
  subtotal?: number;
  ivaAmount?: number;
  orderType?: string;
  deliveryFee?: number;
  currentDiscount?: number;
  appliedPromotions?: any[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:mark-as-paid': [value: boolean];
  'update:selected-payment-method': [value: PaymentMethod];
  'update:cash-received': [value: number];
  'toggle-iva': [enabled: boolean];
}>();

// Get available payment methods from restaurant configuration
const { availablePaymentMethods } = usePaymentMethods();

// Map payment methods to include icons
const iconMap: Record<PaymentMethod, Component> = {
  cash: BanknotesIcon,
  card: CreditCardIcon,
  digital: DevicePhoneMobileIcon,
  other: EllipsisHorizontalIcon
};

const paymentMethods = computed(() => {
  return availablePaymentMethods.value.map(method => ({
    value: method.value,
    label: method.label,
    icon: iconMap[method.value]
  }));
});

const paymentMethodsPanelRef = ref<InstanceType<typeof PaymentMethodsPanel> | null>(null);

const currentCashReceived = computed(() => props.cashReceived || 0);

const changeAmount = computed(() => {
  return currentCashReceived.value - props.orderTotal;
});

watch(() => props.selectedPaymentMethod, (newMethod) => {
  if (newMethod !== 'cash' && currentCashReceived.value !== 0) {
    emit('update:cash-received', 0);
  }
});

watch(() => props.markAsPaid, (newValue) => {
  if (!newValue && currentCashReceived.value !== 0) {
    emit('update:cash-received', 0);
  }
});

function focusCashInput() {
  paymentMethodsPanelRef.value?.focusCashInput();
}

function blurCashInput() {
  paymentMethodsPanelRef.value?.blurCashInput();
}

defineExpose({
  focusCashInput,
  blurCashInput
});
</script>
